import {useEffect} from 'react';
import type {FollowUpRefinementRenderProps} from '@constructor-io/constructorio-ui-asa';
import type {EventBus} from '../host/eventBus';
import type {EventContext} from '../host/types';
import {SuggestedQuestions} from './SuggestedQuestions';

export function hasValidRefinement(refinement: unknown): boolean {
  if (!refinement || typeof refinement !== 'object') return false;
  const candidate = refinement as {question?: unknown; options?: unknown};
  return typeof candidate.question === 'string'
    && candidate.question.trim().length > 0
    && Array.isArray(candidate.options)
    && candidate.options.some(option => typeof option === 'string' && option.trim().length > 0);
}

interface RefinementProps extends FollowUpRefinementRenderProps {
  bus: EventBus;
  context: EventContext;
  displayKey: string;
}

/**
 * Custom-dialog equivalent of Constructor's follow_up_refinement render
 * contract. Constructor owns stream parsing; this component emits one host
 * visibility event once the valid question and options are rendered.
 */
export function FollowUpRefinement({question, options, onOptionClick, isDisabled, bus, context, displayKey}: RefinementProps) {
  const cleanQuestion = typeof question === 'string' ? question.trim() : '';
  const cleanOptions = Array.isArray(options)
    ? options.filter((option): option is string => typeof option === 'string' && option.trim().length > 0).map(option => option.trim())
    : [];
  const valid = Boolean(cleanQuestion && cleanOptions.length);

  useEffect(() => {
    if (!valid) return;
    bus.once(displayKey, () => bus.emit('onRefinementDisplayed', {
      question: cleanQuestion,
      options: cleanOptions,
    }, {...context, source: 'refinement'}));
  }, [bus, cleanOptions, cleanQuestion, context, displayKey, valid]);

  if (!valid) return null;

  return <section className="twe-drinks-advisor__refinement" aria-label={cleanQuestion}>
    <p>{cleanQuestion}</p>
    <SuggestedQuestions
      questions={cleanOptions.map(option => ({label: option, value: option}))}
      onSelect={onOptionClick}
      disabled={isDisabled}
      ariaLabel={cleanQuestion}
    />
  </section>;
}
